import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Table from 'cccisd-table';
import AssessmentActions from './AssessmentActions.js';
import style from '../style.css';
import _pickBy from 'lodash/pickBy';

const ReporterSessions = props => {
    const [reporterSessions, setReporterSessions] = useState([]);
    const [multipleReporterTypes, setMultipleReporterTypes] = useState([]);
    const {
        plan,
        setForceReloadQueries,
        surveyProgress,
        setSurveyProgress,
        isCompleted,
        stoppedDate,
        filters,
        planData,
        progress,
    } = props;
    const pawnInfo = useSelector(state => state.app.share.pawnInfo);

    const pawnMessageList = useMemo(() => {
        const pawnMessages = {};

        const studentMessageList = planData.selectedByPawn.messageList;
        const studentPawn = planData.selectedByPawn.pawn;
        pawnMessages[studentPawn.pawnId] = studentMessageList;

        planData.selectedByPawn.childRoles.contactList.forEach(contact => {
            const contactMessageList = contact.messageList;
            pawnMessages[contact.pawn.pawnId] = contactMessageList;
        });

        return pawnMessages;
    }, [planData]);

    const handleSetReporterSessions = () => {
        let sessions = plan.sessionList;
        if (filters.archived === 'hide') {
            sessions = sessions.filter(s => !s.archivedAt);
        }
        let result = [];

        sessions.forEach(session => {
            const sdi = session.sessionDeploymentInfo;
            if (!sdi || Array.isArray(sdi)) return;
            const filteredSdi = _pickBy(sdi, obj => obj.assigned !== false);

            const numReporters = Object.keys(sdi).length;
            if (numReporters > 1) setMultipleReporterTypes(prev => [...prev, session.settings.reporter]);

            if (Object.keys(filteredSdi).length === 0 && !JSON.parse(plan.settings)?.stopAssessing) {
                // If there are no assigned reporters for the session, and the plan is not stopped, we'll create this placeholder, and show a message in the table
                result.push({
                    id: session.sessionId,
                    reporterType: pawnInfo[sdi[Object.keys(sdi)[0]].pawnId]?.reporterType,
                });
            }

            Object.keys(filteredSdi).forEach(id => {
                const info = {};
                // pawnId ("id") and deploymentId should be good enough to be unique
                //  but some old share functionality would create duplicate pawnIds/deploymentIds across plans/sessions.
                info.id = `${session.sessionId}_${id}_${filteredSdi[id].deploymentId}`;
                info.pawnId = filteredSdi[id].pawnId;
                info.pawnHash = pawnInfo[filteredSdi[id].pawnId]?.pawnHash;
                info.name = pawnInfo[filteredSdi[id].pawnId]?.name;
                info.email = pawnInfo[filteredSdi[id].pawnId]?.email;
                info.group = pawnInfo[filteredSdi[id].pawnId]?.group;
                info.deploymentId = filteredSdi[id].deploymentId;
                info.reporterType = pawnInfo[filteredSdi[id].pawnId]?.reporterType;
                info.options = session.settings.options;
                info.assessmentInfo = session.settings.sessionCollection.map(survey => {
                    return JSON.parse(survey.resource.published_metadata).surveyInfo;
                });
                info.session = session;
                info.assessment = plan;
                info.stopped = JSON.parse(plan.settings)?.stopAssessing || null;
                info.messages = pawnMessageList[filteredSdi[id].pawnId]?.filter(
                    messageData =>
                        messageData.messageType === 'email' &&
                        toString(messageData.pawnId) === toString(id) &&
                        (messageData.action === 'assessment_plan_invite' ||
                            messageData.action === 'assessment_plan_remind') &&
                        messageData.deploymentId === info.deploymentId
                );
                info.numberOfTimesInvited = info.messages?.length;
                info.school = plan.selectedByPawn.parentGroup.site.group.label;

                result.push(info);
            });
        });

        setReporterSessions(result);
        return result;
    };

    useEffect(() => {
        if (pawnInfo) {
            handleSetReporterSessions();
        }
    }, [pawnInfo, planData]);

    const renderTable = () => {
        if (reporterSessions && reporterSessions.length > 0) {
            return (
                <div>
                    <Table
                        columns={[
                            {
                                name: 'reporterType',
                                label: 'Reporter',
                                render: (value, row) => {
                                    return (
                                        <div>
                                            {row.reporterType}
                                            {multipleReporterTypes.includes(row.reporterType) && (
                                                <div
                                                    style={{
                                                        fontSize: 'small',
                                                        fontWeight: '300',
                                                        paddingLeft: '1em',
                                                    }}
                                                >
                                                    {row.name}
                                                </div>
                                            )}
                                        </div>
                                    );
                                },
                            },
                            {
                                name: 'action',
                                label: isCompleted || stoppedDate || plan.archivedDate ? 'Status' : 'Action',
                                render: (value, row) => {
                                    if (!row.assessment) {
                                        return (
                                            <div className="alert alert-warning" role="alert">
                                                There are no {row.reporterType}s assigned. Visit Manage Reporters to
                                                assign someone.
                                            </div>
                                        );
                                    }
                                    return (
                                        <AssessmentActions
                                            row={row}
                                            setForceReloadQueries={setForceReloadQueries}
                                            progress={progress}
                                            surveyProgress={surveyProgress}
                                            setSurveyProgress={setSurveyProgress}
                                        />
                                    );
                                },
                            },
                            {
                                name: 'settings',
                                label: 'Assessments',
                                render: (value, row) => {
                                    if (!row.assessment) {
                                        return null;
                                    }
                                    return (
                                        <div>
                                            {row.options.map(surveyHandle => {
                                                const surveyInfo = row.assessmentInfo.find(
                                                    a => a.surveyHandle === surveyHandle
                                                );

                                                const surveyStoppedAndIncomplete = () => {
                                                    if (row.stopped) {
                                                        if (
                                                            surveyProgress &&
                                                            surveyProgress[`${row.session.sessionId}_${row.pawnId}`] &&
                                                            surveyProgress[`${row.session.sessionId}_${row.pawnId}`][
                                                                surveyHandle
                                                            ]
                                                        ) {
                                                            return (
                                                                surveyProgress[
                                                                    `${row.session.sessionId}_${row.pawnId}`
                                                                ][surveyHandle] !== 2
                                                            );
                                                        }
                                                        return true;
                                                    }
                                                    return false;
                                                };

                                                return (
                                                    <div key={surveyHandle}>
                                                        <span
                                                            style={
                                                                surveyStoppedAndIncomplete()
                                                                    ? { textDecoration: 'line-through' }
                                                                    : {}
                                                            }
                                                        >
                                                            {surveyInfo ? surveyInfo.surveyLabel : ''}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                },
                            },
                        ]}
                        data={reporterSessions}
                        className="table"
                        showPerPageOptions={false}
                        hideShowingRowsSummary
                    />
                </div>
            );
        }
    };

    return <div className={style.reporterSessions}>{renderTable()}</div>;
};

export default ReporterSessions;

ReporterSessions.propTypes = {
    plan: PropTypes.object.isRequired,
    setForceReloadQueries: PropTypes.func.isRequired,
    surveyProgress: PropTypes.object,
    setSurveyProgress: PropTypes.func.isRequired,
    isCompleted: PropTypes.bool,
    stoppedDate: PropTypes.string,
    filters: PropTypes.shape({
        ready: PropTypes.bool,
        stopped: PropTypes.bool,
        completed: PropTypes.bool,
        assessments: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
        archived: PropTypes.string,
    }),
    planData: PropTypes.object,
    progress: PropTypes.array,
};

ReporterSessions.defaultProps = {};
